// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/** Assembling the artefacts. */

import type { Decision, Group, ImageRecord, DatasetOptions } from './core/types.ts';
import {
  CSV_BOM,
  IMAGES_DIR,
  captionBytes,
  datasetToml,
  embeddingManifest,
  metadataLine,
  planExport,
  reportCsv,
  type ExportPlan,
} from './core/dataset.ts';
import type { ZipEntry } from './io/zipwriter.ts';
import { EMBED_DIM, MODEL_DTYPE, MODEL_ID, MODEL_REVISION } from './model/config.mjs';

const enc = (s: string): Uint8Array => new TextEncoder().encode(s);

export interface ExportInputs {
  readonly records: readonly ImageRecord[];
  readonly decisions: readonly Decision[];
  readonly groups: readonly Group[];
  readonly kept: readonly number[];
  readonly files: ReadonlyMap<number, File>;
  readonly options: DatasetOptions;
  readonly embeddings: Float32Array | null;
  readonly includeEmbeddings: boolean;
}

export interface ExportSummary {
  readonly plan: ExportPlan;
  readonly entryCount: number;
  readonly payloadBytes: number;
}

export function summarise(input: ExportInputs): ExportSummary {
  const byId = new Map(input.records.map((r) => [r.id, r]));
  const kept = input.kept.map((id) => byId.get(id)!).filter(Boolean);
  const plan = planExport(kept, input.options);
  let payloadBytes = 0;
  for (const r of kept) payloadBytes += r.bytes;
  // images + captions + metadata.jsonl + report + toml + README, and the two embedding files.
  const extras = 5 + (input.includeEmbeddings && input.embeddings ? 2 : 0);
  return { plan, entryCount: kept.length * 2 + extras, payloadBytes };
}

/**
 * The archive contents, in order.
 *
 * README first so it is the first thing in a listing, then the photographs, then the reports. The
 * images are yielded as `File` handles rather than bytes so the ZIP writer can slice them — a
 * 1,400-photograph export never has more than one image resident.
 */
export async function* exportEntries(input: ExportInputs, summary: ExportSummary): AsyncGenerator<ZipEntry> {
  const { plan } = summary;
  const byId = new Map(input.records.map((r) => [r.id, r]));
  const groupSize = new Map<number, number>();
  for (const g of input.groups) for (const m of g.members) groupSize.set(m, g.members.length);

  yield { name: 'README.md', data: enc(readme(input, summary)) };
  yield { name: 'dataset.toml', data: enc(datasetToml(plan)) };

  const dir = `${IMAGES_DIR}/${plan.conceptDir}`;
  const metadata: string[] = [];

  for (const id of input.kept) {
    const rec = byId.get(id);
    const file = input.files.get(id);
    const name = plan.names.get(id);
    if (!rec || !file || !name) continue;

    yield { name: `${dir}/${name}`, data: file };

    // The caption file's stem must match the image's stem exactly — sd-scripts pairs them by
    // replacing the extension, so `photo.jpg` looks for `photo.txt` and nothing else.
    const stem = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;
    yield { name: `${dir}/${stem}.txt`, data: captionBytes(plan.caption) };

    metadata.push(metadataLine(rec, name, plan.caption, groupSize.get(id) ?? 1));
  }

  yield { name: `${dir}/metadata.jsonl`, data: enc(metadata.join('')) };

  let csv = CSV_BOM;
  for (const row of reportCsv({
    records: input.records,
    decisions: input.decisions,
    groups: input.groups,
    plan,
  })) {
    csv += row;
  }
  yield { name: 'dedup-report.csv', data: enc(csv) };

  if (input.includeEmbeddings && input.embeddings) {
    const rows: number[] = [];
    const paths: string[] = [];
    // Every analysed image, not only the keepers — the point of shipping the matrix is to
    // re-cluster at a different threshold later, which needs the images that were dropped too.
    for (const r of input.records) {
      rows.push(r.id);
      paths.push(r.path);
    }
    const bytes = new Uint8Array(
      input.embeddings.buffer,
      input.embeddings.byteOffset,
      input.embeddings.byteLength,
    );
    yield { name: 'embeddings.f32', data: new Uint8Array(bytes) };
    yield {
      name: 'embeddings.json',
      data: enc(embeddingManifest(rows, paths, EMBED_DIM, `${MODEL_ID}@${MODEL_REVISION} (${MODEL_DTYPE})`)),
    };
  }
}

function readme(input: ExportInputs, summary: ExportSummary): string {
  const { plan } = summary;
  const dropped = input.decisions.filter((d) => d.verdict !== 'kept').length;
  const dir = `${IMAGES_DIR}/${plan.conceptDir}`;
  const warn =
    plan.unsupported.length > 0
      ? `\n> **${plan.unsupported.length} image(s) here use a format kohya does not read by default.**\n` +
        `> sd-scripts globs \`.png .jpg .jpeg .webp .bmp\`; anything else needs an optional Pillow\n` +
        `> plugin. They are included because they are yours, but convert them before training.\n`
      : '';

  return `# ${plan.conceptDir}

Prepared by [loraprep](https://loraprep.benrichardson.dev) on ${new Date().toISOString().slice(0, 10)}.
${input.records.length} images in, ${input.kept.length} kept, ${dropped} set aside.
${warn}
## What is here

\`\`\`
${dir}/            your images, plus one .txt caption each, plus metadata.jsonl
dataset.toml               kohya config for the --dataset_config path
dedup-report.csv           every input image and what happened to it
${input.includeEmbeddings && input.embeddings ? 'embeddings.f32 / .json     the raw vectors, so you can re-cluster without re-embedding\n' : ''}\`\`\`

## kohya_ss / sd-scripts

Point \`--train_data_dir\` at \`${IMAGES_DIR}\` — the PARENT of the numbered folder, not the folder
itself. sd-scripts reads \`${plan.conceptDir}\` as ${plan.repeats} repeats of the concept
\`${plan.caption}\`.

\`\`\`bash
accelerate launch train_network.py \\
  --train_data_dir ./${IMAGES_DIR} \\
  --caption_extension .txt \\
  ...
\`\`\`

Or with the config, which does not read the folder name at all and takes the numbers from the TOML:

\`\`\`bash
accelerate launch train_network.py --dataset_config dataset.toml ...
\`\`\`

## Hugging Face datasets / diffusers

\`\`\`python
from datasets import load_dataset
ds = load_dataset("imagefolder", data_dir="${dir}")
\`\`\`

\`metadata.jsonl\` carries the caption under BOTH \`text\` and \`prompt\`, because diffusers has no one
convention: \`train_text_to_image_lora.py\` defaults \`--caption_column\` to \`text\`, while the
DreamBooth scripts and \`convert_to_imagefolder.py\` use \`prompt\`.

## The captions are a starting point, not an answer

Every image here carries the same caption. That is correct for a single-subject LoRA and wrong for
a style or a varied set — edit the \`.txt\` files, keeping the trigger word first.

## What was set aside, and why it is not gone

Nothing was deleted. \`dedup-report.csv\` lists every image you supplied with a verdict, the file it
was judged against, and the numbers behind the decision. The originals are still on your disk.
`;
}
